import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useLoadingStore } from '@/modules/core/stores/loadingStore'
import { useErrorManagerComposable } from '@/modules/core/composables/errorManagerComposable'
import { useNavigation } from '@/modules/core/composables/navigationComposable'
import { useToastStore } from '@/modules/core/stores/toastStore'
import { ButtonTypeEnum, ErrorDisplayTypeEnum, UserRoleEnum } from '@/modules/core/models/enums'

import ENDPOINTS from '@/modules/core/constants/apiEndpoints'
import ServiceSchemaError from '@/modules/core/models/errors/ServiceSchemaError'

import MiddlewareService from '@/modules/core/services/MiddlewareService'
import DXPService from '@/modules/core/services/DXPService'
import ServiceError from '@/modules/core/models/errors/ServiceError'
import ModalButton from '@/modules/core/models/modalMessage/ModalButton'
import Insurance from '@/modules/common/common/models/APP/Insurance'
import RaterPriceDentalResponse from '@/modules/common/common/models/MID/responses/RaterPriceResponse'
import GetProductResponse from '@/modules/common/common/models/DXP/responses/GetProductResponse'

import DentalConfigurations from '@/modules/dental/common/models/APP/DentalConfigurations'
import DentalInsurance from '@/modules/dental/common/models/APP/DentalInsurance'
import DentalRaterAnswers from '@/modules/dental/rater/models/APP/DentalRaterAnswers'
import DentalCommonMapper from '@/modules/dental/common/mappers'
import DentalMapper from '@/modules/dental/rater/mappers/index'
import CommonMapper from '@/modules/common/common/mappers/index'
import ProductId from '@/modules/common/common/models/DXP/ProductId'
import type DXPProduct from '@/modules/common/common/models/DXP/Product'
import type RaterPricing from '@/modules/common/common/models/MID/RaterPricing'
import type BaseRaterStore from '@/modules/common/rater/models/APP/BaseRaterStore'
import PAGES from '@/modules/dental/rater/constants/pages'
import type FormPage from '@/modules/common/common/models/APP/FormPage'
import ConsoleError from '@/modules/core/models/errors/ConsoleError'
import GTMEventType from '@/modules/core/models/GTMEventType'
import { useGTMStore } from '@/modules/core/stores/gtmStore'
import { useSharedGtmDataStore } from '@/modules/core/stores/sharedGtmDataStore'
import { useSupportModalStore } from '@/modules/core/stores/supportModalStore'
import { useCommonStore } from '@/modules/core/stores/commonStore'
import { useI18n } from 'vue-i18n'

export const useDentalStore = defineStore('raterDental', () => {
  const errorManager = useErrorManagerComposable()
  const toastStore = useToastStore()
  const loadingStore = useLoadingStore()
  const router = useRouter()
  const { pushPreservingMCode } = useNavigation()
  const supportModalStore = useSupportModalStore()
  const commonStore = useCommonStore()
  const gmtStore = useGTMStore()
  const sharedGtmDataStore = useSharedGtmDataStore()
  const { t } = useI18n()

  const globalConfig = ref(new DentalConfigurations())
  const raterAnswers = ref(new DentalRaterAnswers())
  const insuranceProposals = ref<DentalInsurance[]>([])

  function getPages(): Record<string, FormPage> {
    return PAGES
  }

  function getBranchConfiguration() {
    return globalConfig.value
  }

  function getInsuranceProposals() {
    return insuranceProposals.value
  }

  function clearRaterAnswers() {
    raterAnswers.value = new DentalRaterAnswers()
  }

  function getRateFormAnswers() {
    return raterAnswers.value as DentalRaterAnswers
  }

  function setRateFormAnswers(answers: Partial<DentalRaterAnswers>) {
    if (!answers) return

    raterAnswers.value.updateAttributes(answers)
  }

  function getMostRelevantProducts(insurances: DentalInsurance[]) {
    const sortedItems = insurances.sort((a, b) => a.order - b.order)

    return sortedItems.slice(0, 2)
  }

  async function loadConfiguration() {
    try {
      const endpoint = ENDPOINTS.DXP_GET_BRANCH_DENTAL
      const { error, data } = await DXPService.get(endpoint)
      if (error) {
        throw new ServiceError(data)
          .setDisplayType(ErrorDisplayTypeEnum.MODAL)
          .setDisplayType(ErrorDisplayTypeEnum.MODAL)
          .setModalDescription('copy_error_service_description')
          .setButtons([
            new ModalButton({
              text: 'header.help-text',
              icon: 'icon-message-text-01',
              type: ButtonTypeEnum.OUTLINE,
              onclick: () => {
                gmtStore.sendEvent({
                  event: GTMEventType.CALLMEBACK_BTN,
                  click_name: 'support-modal-btn',
                  click_id: 'support-modal-btn',
                  btn_label: t('header.help-text'),
                  value: sharedGtmDataStore.policyValue
                })
                supportModalStore.setNavTo('exit')
                supportModalStore.setOpenModal(true)
              }
            }),
            new ModalButton({
              text: 'copy_button_return',
              navTo: 'exit',
              type: ButtonTypeEnum.OUTLINE
            })
          ])
      }

      globalConfig.value = new DentalConfigurations(data)
      raterAnswers.value.businessLine = globalConfig.value.businessLineId
    } catch (error) {
      loadingStore.stopLoading()
      errorManager.handleError(error)
    }
  }

  async function sendRate(
    options: {
      preventRouting?: boolean
      answers?: DentalRaterAnswers | undefined
      externalErrorHandling?: boolean
      checkLocation?: string
    } = {}
  ) {
    options.preventRouting = Boolean(options.preventRouting)
    options.externalErrorHandling = Boolean(options.externalErrorHandling)
    if (!options.checkLocation) {
      options.checkLocation = location.pathname
    }

    try {
      const loadingTexts = ['common.loading.processing', 'common.loading.looking-for-best-products']
      loadingStore.startLoading(...loadingTexts)

      const endpoint = ENDPOINTS.RATER_CALCULATE_PRICE_DENTAL
      const answers = options.answers ?? (raterAnswers.value as DentalRaterAnswers)

      const request = DentalMapper.GetPricingMapper.toRequest(
        answers,
        globalConfig.value.relationships,
        true,
        true,
        commonStore.infoMediator?.id
      )
      const { error, data } = await MiddlewareService.post(endpoint, request)
      if (error) {
        throw new ServiceError(data).setDisplayType(ErrorDisplayTypeEnum.MODAL)
      }

      if (options.answers) {
        raterAnswers.value.updateAttributes(options.answers)
      }

      const response = new RaterPriceDentalResponse(data)
      if (response.listPricing.length === 0) {
        throw new ServiceSchemaError()
          .setTitle('copy_rater-empty-error-modal_title')
          .setModalDescription('copy_rater-empty-error-modal_description')
          .setButtons([
            new ModalButton({
              text: 'components.InsuredSubheaderComponent.button'
            }),
            new ModalButton({
              text: 'copy_button_init_return',
              navTo: 'rater-nav',
              type: ButtonTypeEnum.OUTLINE
            })
          ])
      }

      setRateFormAnswers({
        quotationId: response.quotationId,
        businessLine: response.listPricing[0].businessLine
      })
      const products = data.listPricing.map((l: RaterPricing) => {
        return new ProductId({
          productCode: l.productCode,
          clientCode: l.clientCode
        })
      })
      const dxpData = await _fetchDXPInsurances(products)
      insuranceProposals.value = _loadCompleteSheetProducts(dxpData, data.listPricing)

      if (insuranceProposals.value.length === 0) {
        const modalButtons = [
          new ModalButton({
            text: 'components.InsuredSubheaderComponent.button'
          }),
          new ModalButton({
            text: 'copy_button_init_return',
            navTo: 'rater-nav',
            type: ButtonTypeEnum.OUTLINE
          })
        ]
        throw new ServiceSchemaError()
          .setTitle('copy_rater-empty-error-modal_title')
          .setModalDescription('copy_rater-empty-error-modal_description')
          .setButtons(modalButtons)
      }

      if (!options.preventRouting && options.checkLocation === location.pathname) {
        // check pathname to prevent to push a route when user clicks to go to back or other stuff
        await pushPreservingMCode(router, { name: `dental-insurance-proposals` })
      }
    } catch (error) {
      if (options.externalErrorHandling) {
        throw error
      }

      errorManager.handleError(error)
    } finally {
      loadingStore.stopLoading()
    }
  }

  //** PRIVATE SUPPORT METHODS */
  async function _fetchDXPInsurances(products: ProductId[]) {
    const endpoint = ENDPOINTS.DXP_GET_PRODUCT
    const request = DentalCommonMapper.GetProductsMapper.toRequest(products)
    const { error, data } = await DXPService.post(endpoint, request)
    if (error) throw new ServiceError(data)

    return data.map((d: any) => new GetProductResponse(d).product)
  }

  function _loadCompleteSheetProducts(
    DXPProducts: DXPProduct[],
    MIDProducts: RaterPricing[]
  ): DentalInsurance[] {
    const products: DentalInsurance[] = []
    DXPProducts.forEach((DXPProduct) => {
      const MIDProduct = MIDProducts.find((p) => {
        return String(DXPProduct.productId) === String(p.productCode)
      })

      if (MIDProduct) {
        const insurance = DentalCommonMapper.GetProductsMapper.fromResponse(DXPProduct, MIDProduct)
        products.push(insurance)
      }
    })

    return products
  }

  async function hireProduct(
    quotationId: string | null,
    startDate: Date,
    billingCycle: string,
    product: Insurance,
    role: UserRoleEnum
  ): Promise<{ valid: boolean }> {
    let valid = true

    try {
      loadingStore.startLoading('common.loading.processing')

      const { error, data } = await _saveQuotation(
        quotationId,
        startDate,
        billingCycle,
        product,
        role
      )
      if (error) throw new ServiceError(data)
    } catch (error) {
      console.error(error)
      valid = false
    } finally {
      loadingStore.stopLoading()
    }

    return { valid }
  }

  async function loadSpecificProduct(productCode: string, clientCode: string) {
    try {
      const dxpData = await _fetchDXPInsurances([{ productCode, clientCode }])
      if (!dxpData.length) {
        throw new ConsoleError({
          message: `DXP Error: No se ha encontrado la ficha para el producto ${productCode}`
        })
      }

      const DXPProduct = dxpData[0]
      const insurance = DentalCommonMapper.GetProductsMapper.fromResponse(DXPProduct, {})

      raterAnswers.value = new DentalRaterAnswers({
        product: insurance,
        businessLine: globalConfig.value.businessLineId
      })
    } catch (error) {
      errorManager.handleError(error)
    }
  }

  async function _saveQuotation(
    quotationId: string | null,
    startDate: Date,
    billingCycle: string,
    product: Insurance,
    holderRole: UserRoleEnum
  ) {
    if (!quotationId) throw new Error('Quotation id is required')

    const endpoint = ENDPOINTS.SAVE_QUOTATION_DENTAL
    const request = CommonMapper.SaveQuotationMapper.toRequest(
      quotationId,
      startDate,
      billingCycle,
      product,
      holderRole
    )
    const { error, data } = await MiddlewareService.post(endpoint, request)
    return { error, data }
  }

  async function sendBudget(
    quotationNumber: string,
    email: string,
    privacyPolicyConsent: boolean,
    quotationId: string | null,
    startDate: Date,
    billingCycle: string,
    product: Insurance,
    holderRole: UserRoleEnum
  ) {
    try {
      loadingStore.startLoading('common.loading.processing')
      const quotationResult = await _saveQuotation(
        quotationId,
        startDate,
        billingCycle,
        product,
        holderRole
      )
      if (quotationResult.error) {
        throw new ServiceError(quotationResult.data)
          .setDisplayType(ErrorDisplayTypeEnum.TOAST_IN_MODAL)
          .setTitle('copy_budget-service_error')
      }
      const endpoint = ENDPOINTS.SEND_BUDGET
      const request = { quotationNumber, to: email, privacyPolicyConsent }
      const { error, data } = await MiddlewareService.post(endpoint, request)
      if (error) {
        throw new ServiceError(data)
          .setDisplayType(ErrorDisplayTypeEnum.TOAST_IN_MODAL)
          .setTitle('copy_budget-service_error')
      }

      toastStore.successToast('copy_budget-service_success')
    } catch (error) {
      errorManager.handleError(error)
      throw error
    } finally {
      loadingStore.stopLoading()
    }
  }

  const store: BaseRaterStore<DentalInsurance, DentalRaterAnswers, DentalConfigurations> = {
    getPages,
    getBranchConfiguration,
    clearRaterAnswers,
    getInsuranceProposals,
    setRateFormAnswers,
    getRateFormAnswers,
    getMostRelevantProducts,

    loadConfiguration,
    loadSpecificProduct,
    hireProduct,
    sendRate,
    sendBudget
  }

  return store
})
